import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { KeyRound, Loader2, MailCheck } from "lucide-react";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost("/auth/forgot-password", { email });
      setDone(true);
    } catch {
      // Backend never throws to avoid email enumeration
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-4" data-testid="forgot-success">
        <Card className="w-full max-w-md border bg-card">
          <CardContent className="pt-6 text-center space-y-3">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
              <MailCheck className="h-7 w-7" />
            </div>
            <h2 className="font-display text-lg font-semibold">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              If an account with <span className="font-medium text-foreground">{email}</span> exists,
              we have sent a password-reset link. The link expires in 1 hour.
            </p>
            <Button asChild className="w-full mt-2">
              <Link to="/login" data-testid="forgot-back-login">Back to Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border bg-card">
        <CardHeader className="text-center space-y-1">
          <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
            <KeyRound className="h-6 w-6" />
          </div>
          <CardTitle className="font-display text-xl">Forgot password</CardTitle>
          <CardDescription>We'll send you a reset link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                data-testid="forgot-email"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="forgot-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Remembered it?{" "}
            <Link to="/login" className="text-[hsl(var(--primary))] hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
